export type ShopSignupBonusStatus = 'inactive' | 'active' | 'in_review' | 'completed' | 'expired';

/** Ops labels match API: inactive → active → in_review → completed | expired. */
export function shopSignupBonusBadge(status: ShopSignupBonusStatus | string): {
  label: string;
  cls: string;
} {
  switch (status) {
    case 'inactive':
      return { label: 'Inactive', cls: 'bg-violet-100 text-violet-800 hover:bg-violet-200' };
    case 'active':
      return { label: 'Active', cls: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' };
    case 'in_review':
      return { label: 'In review', cls: 'bg-amber-100 text-amber-900 hover:bg-amber-200' };
    case 'completed':
      return { label: 'Completed', cls: 'bg-sky-100 text-sky-800 hover:bg-sky-200' };
    case 'expired':
      return { label: 'Expired', cls: 'bg-gray-100 text-gray-600 hover:bg-gray-200' };
    default:
      return { label: String(status), cls: 'bg-gray-100 text-gray-600 hover:bg-gray-200' };
  }
}

export function shopInactivityRuleText(input: {
  status: ShopSignupBonusStatus;
  grantedAt?: string | null;
  expiresAt?: string | null;
}): string {
  if (!input.grantedAt) return 'Starts after the Player claims';
  if (input.status === 'expired') return 'Expired (grant recalled)';
  if (input.status === 'completed') return 'N/A — already completed';
  if (input.status === 'in_review') {
    return 'Locked in review — does not expire until the first payout is approved';
  }
  const ms = input.expiresAt
    ? new Date(input.expiresAt).getTime() - new Date(input.grantedAt).getTime()
    : NaN;
  const days = Number.isFinite(ms) && ms > 0 ? Math.max(1, Math.round(ms / 86400000)) : null;
  const window = days != null ? `${days}-day` : 'Configured';
  return `${window} idle window — checked on login; full grant recall required before Expired`;
}

/** Guard: never PATCH opt-in while /shop/me value is still unknown (null). */
export function canSaveSignupBonusOptIn(optIn: boolean | null): optIn is boolean {
  return optIn !== null;
}

export type ShopSignupBonusOverrideFields = {
  amountUsd: number | null;
  winThresholdUsd: number | null;
  expiryDays: number | null;
  dailyGrantBudget: number | null;
};

export type ShopSignupBonusSettings = {
  enabled: boolean;
  overrides: ShopSignupBonusOverrideFields;
  effective: ShopSignupBonusOverrideFields | null;
  defaults: ShopSignupBonusOverrideFields;
};

export function parseShopSignupBonusSettings(
  shop: Record<string, unknown> | null | undefined
): ShopSignupBonusSettings | null {
  const raw = shop?.signupBonusSettings;
  if (!raw || typeof raw !== 'object') return null;
  const settings = raw as Record<string, unknown>;
  const pickOverrides = (obj: unknown): ShopSignupBonusOverrideFields => {
    const o = (obj && typeof obj === 'object' ? obj : {}) as Record<string, unknown>;
    const num = (v: unknown) =>
      v != null && Number.isFinite(Number(v)) ? Number(v) : null;
    return {
      amountUsd: num(o.amountUsd),
      winThresholdUsd: num(o.winThresholdUsd),
      expiryDays: num(o.expiryDays),
      dailyGrantBudget: num(o.dailyGrantBudget),
    };
  };
  return {
    enabled: settings.enabled === true,
    overrides: pickOverrides(settings.overrides),
    effective: settings.effective ? pickOverrides(settings.effective) : null,
    defaults: pickOverrides(settings.defaults ?? settings.systemDefaults),
  };
}

export function overrideInputValue(value: number | null | undefined): string {
  return value != null && Number.isFinite(value) ? String(value) : '';
}

export function parseOverrideInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export function parseShopMeSignupBonus(shop: Record<string, unknown> | null | undefined): {
  optIn: boolean | null;
  grantCount: number | null;
  settings: ShopSignupBonusSettings | null;
} {
  if (!shop) return { optIn: null, grantCount: null, settings: null };
  const optIn = shop.signupBonusOptIn === true;
  const raw = shop.signupBonusGrantCount;
  const grantCount =
    raw != null && Number.isFinite(Number(raw)) ? Number(raw) : null;
  return {
    optIn,
    grantCount,
    settings: parseShopSignupBonusSettings(shop),
  };
}
